import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  commonUtils,
  IActionConfig,
  useNotifications,
  useEditorStore,
  useFlowBuilder2AddFlowActionMutation,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';

export const useAddAction = (
  options?: Parameters<typeof useFlowBuilder2AddFlowActionMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const notifications = useNotifications();

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);

  const { mutateAsync, isPending: isAddingAction } = useFlowBuilder2AddFlowActionMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      updateFlow((draft) => {
        draft.steps = data;
        return draft;
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.addActionFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const addAction = useCallback(
    async (actionSetId: string, value: Partial<IActionConfig>) => {
      const flow = getFlow();
      if (flow) {
        const config = flow.steps.find((step) => step.actionSetId === actionSetId);
        if (config) {
          return mutateAsync({
            flowId: flow.id,
            flowStepId: config.id,
            data: value,
          });
        }
      }
    },
    [getFlow, mutateAsync],
  );

  return { addAction, isAddingAction };
};
